import { Component, Injector, OnDestroy, OnInit, signal, ViewChild, inject } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { type BaseAppStateService } from '@modules/common/services/base-app-state.service';
import { FormChangesModalsService } from '@modules/common/services/form-changes-modals.service';
import { MainViewService } from '@modules/common/services/main-view.service';
import { ModalService } from '@modules/modal/services/modal.service';
import type { Stitch } from '@modules/common/types/stitch';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  template: '',
  standalone: false,
})
export abstract class AbstractMainViewComponent<
    STITCH_TYPE extends StitchType,
    T extends Stitch,
    F extends FullFormBaseComponent<STITCH_TYPE, T, Record<string, AbstractControl>>,
    ListState extends string = string,
  >
  implements OnInit, OnDestroy
{
  public item: T;
  public StitchType = StitchType;

  @ViewChild('formRef', { static: false }) formComponent?: F;

  protected readonly formChangesModalsService = inject(FormChangesModalsService);
  private readonly mainViewService = inject(MainViewService);
  private readonly createInjector = inject(Injector);

  protected abstract stateService: BaseAppStateService<T, T, string, ListState>;

  /**
   * Which list-pane section the sidebar currently shows ('items' section vs. the second-level
   * container list, e.g. 'notes' vs 'notebooks') — lets the empty state (nothing selected) show
   * "No note selected" vs "No notebook selected" copy without the main-view needing its own
   * section-tracking state. Populated in `ngOnInit`, not as a field initializer — `stateService`
   * is abstract here and only assigned by the subclass's own field, which runs after this class's.
   *
   * Reads `getSection()`, not `getTabs()` — Flagged/Archived/Search Results/etc. all share one
   * `getTabs()` value ('tabs'), so the concrete tab (e.g. 'notes' vs 'notebooks') must come from the
   * list container's own tab-switcher state, which `getSection()` folds in. `getTabs()` alone would
   * misreport the section for every one of those folders.
   */
  protected readonly section = signal<ListState | undefined>(undefined);

  protected readonly alive = new Subject<void>();
  private closeAttempt: AbortController | undefined;

  public ngOnInit(): void {
    this.stateService
      .getSection()
      .pipe(takeUntil(this.alive))
      .subscribe((tabs) => this.section.set(tabs));

    this.stateService
      .getMainView()
      .pipe(
        takeUntil(this.alive),
        // Restoring the visible item after Stay must not ask about that same editor again.
        filter((item) => item !== this.item),
        switchMap((item) => {
          this.closeAttempt?.abort();
          this.closeAttempt = undefined;
          return this.formChangesModalsService
            .confirmUnsavedChanges(item?.id, true)
            .pipe(map((canLeave) => ({ canLeave, item })));
        }),
        tap(({ canLeave }) => {
          if (!canLeave) this.stateService.setMainView(this.item);
        }),
        filter(({ canLeave }) => canLeave),
        map(({ item }) => item),
      )
      .subscribe((item) => (this.item = item));
  }

  public handleFormClose(shouldClose = true): void {
    if (this.closeAttempt) return;
    const item = this.item;
    const attempt = new AbortController();
    this.closeAttempt = attempt;
    this.formChangesModalsService.confirmUnsavedChanges(item?.id, false, attempt.signal)
      .pipe(takeUntil(this.alive))
      .subscribe({
        next: (canLeave) => {
          if (canLeave && shouldClose && !attempt.signal.aborted && this.item === item) {
            this.stateService.setMainView(null);
            // Its card in the list deselects too (the open highlight goes with the panel).
            if (item) this.mainViewService.announceClosed(item);
          }
        },
        complete: () => {
          if (this.closeAttempt === attempt) this.closeAttempt = undefined;
        },
      });
  }

  /**
   * The empty state's "Create…" button: the same New … modal the rail's + New and Add open, so the item
   * gets the full form and its tabs while this panel stays empty behind it (#2694). `ModalService` is
   * provided by the layout, not at root, so it is looked up on click.
   */
  protected openCreateModal(item: Stitch): void {
    this.createInjector.get(ModalService).openFullFormOnFirstTab(item, this.createInjector);
  }

  public ngOnDestroy(): void {
    this.closeAttempt?.abort();
    this.alive.next();
    this.alive.complete();
  }
}
